import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { createAdminUser, updateAdminUser } from "@/lib/api";
import { getEmployeeProfile, saveEmployeeProfile, uploadIdPhoto, getCommissionGroups } from "@/lib/employeeApi";
import { setPath } from "./formBits";
import { GeneralTab, Credentials } from "./GeneralTab";
import { HrTab } from "./HrTab";
import { DocumentsTab } from "./DocumentsTab";
import { NotesTab } from "./NotesTab";
import { HistoryTab } from "./HistoryTab";

const DEFAULT_BENEFITS = [{ name: "Vacation days", given: 0, used: 0 }, { name: "Sick days", given: 0, used: 0 }, { name: "Personal days", given: 0, used: 0 }];
const blank = () => ({
  first_name: "", last_name: "", email: "", phone: "", password: "", role_id: "", super_admin: false,
  profile: { address: {}, emergency_contact: {} }, id_doc: {}, employment_status: "active", hr: { benefits: DEFAULT_BENEFITS },
});

// mode: "create" | "edit" | "self" (read-only view of your own General + HR)
export const UserDialog = ({ open, onClose, target, mode, roles, meSuper, onSaved }) => {
  const [form, setForm] = useState(blank());
  const [meta, setMeta] = useState({});
  const [photo, setPhoto] = useState(null);
  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const ro = mode === "self";
  const set = (path, v) => setForm((f) => setPath(f, path, v));

  useEffect(() => {
    if (!open) return;
    setTab("general"); setPhoto(null); setMeta({}); setCreatedId(null);
    getCommissionGroups().then(setGroups).catch(() => {});
    if (!target) { setForm(blank()); return; }
    const base = { ...blank(), first_name: target.first_name, last_name: target.last_name, email: target.email,
      phone: target.phone || "", role_id: target.role_id || "", super_admin: !!target.super_admin };
    setForm(base); setLoading(true);
    getEmployeeProfile(target.id)
      .then((p) => { setForm({ ...base, profile: p.profile, id_doc: p.id_doc, employment_status: p.employment_status, hr: p.hr }); setMeta(p); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load employee details"))
      .finally(() => setLoading(false));
  }, [open, target]);

  const validate = () => {
    if (!form.first_name || !form.last_name) return ["First name and last name are required", "general"];
    if (!form.email) return ["Enter a work email", "access"];
    if (mode === "create" && !form.password) return ["Enter a password", "access"];
    if (!form.super_admin && (mode === "create" || target?.role === "staff") && !form.role_id) return ["Pick a security role", "access"];
    if (form.employment_status === "inactive" && !form.hr?.separation_date) return ["Enter a separation date for inactive employees", "general"];
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err[0]); setTab(err[1]); return; }
    setSaving(true);
    try {
      const basics = { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, role_id: form.role_id || null };
      if (meSuper) basics.super_admin = form.super_admin;
      let id = target?.id || createdId;
      if (!id) { id = (await createAdminUser({ ...basics, password: form.password })).id; setCreatedId(id); }
      else await updateAdminUser(id, form.password && !createdId ? { ...basics, password: form.password } : basics);
      await saveEmployeeProfile(id, { profile: form.profile, id_doc: form.id_doc, employment_status: form.employment_status, hr: form.hr });
      if (photo) await uploadIdPhoto(id, photo);
      toast.success(mode === "create" ? "User created" : "User updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save user"); }
    setSaving(false);
  };

  const later = mode === "create";
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">{mode === "create" ? "Create New User" : mode === "self" ? "My information" : `Edit ${target?.first_name || "User"}`}</DialogTitle>
          <DialogDescription>{ro ? "This is your own record. Ask a manager to change anything." : "Employee details, HR, documents and notes."}</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full justify-start" data-testid="user-dialog-tabs">
              <TabsTrigger value="general" data-testid="user-tab-general">General</TabsTrigger>
              <TabsTrigger value="access" data-testid="user-tab-access">System Accessibility</TabsTrigger>
              <TabsTrigger value="hr" data-testid="user-tab-hr">Human Resources</TabsTrigger>
              {!ro && <TabsTrigger value="documents" disabled={later} data-testid="user-tab-documents">Documents</TabsTrigger>}
              {!ro && <TabsTrigger value="notes" disabled={later} data-testid="user-tab-notes">Notes</TabsTrigger>}
              {!ro && <TabsTrigger value="history" disabled={later} data-testid="user-tab-history">Change History</TabsTrigger>}
            </TabsList>
            {later && <p className="text-xs text-slate-400 mt-2">Documents, notes and change history open after the user is created.</p>}
            <TabsContent value="general" className="mt-5">
              <GeneralTab form={form} set={set} ro={ro} userId={target?.id} hasPhoto={meta.has_id_photo} onPhoto={setPhoto} unmasked={meta.unmasked ?? true} />
            </TabsContent>
            <TabsContent value="access" className="mt-5">
              <Credentials form={form} set={set} ro={ro} editing={mode !== "create"} roles={roles} meSuper={meSuper} isSelfView={ro} />
            </TabsContent>
            <TabsContent value="hr" className="mt-5"><HrTab form={form} set={set} ro={ro} groups={groups} /></TabsContent>
            {!ro && !later && <TabsContent value="documents" className="mt-5"><DocumentsTab userId={target.id} types={meta.doc_types || ["Other"]} /></TabsContent>}
            {!ro && !later && <TabsContent value="notes" className="mt-5"><NotesTab userId={target.id} /></TabsContent>}
            {!ro && !later && <TabsContent value="history" className="mt-5"><HistoryTab key={String(open)} userId={target.id} /></TabsContent>}
          </Tabs>
        )}
        {!ro && !loading && ["general", "access", "hr"].includes(tab) && (
          <Button onClick={save} disabled={saving} className="w-full bg-teal hover:bg-teal-dark text-white mt-2" data-testid="submit-user-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "create" ? "Create User" : "Save Changes"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
